import React, { useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, MetricCard, Section } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { Download, FileDown, TrendingUp, Users, Briefcase, PiggyBank, Info, Copy, PlayCircle, RotateCcw, Link2, RefreshCw, Send, Recycle } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Reports() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [potCommun, setPotCommun] = useState(false);
  const [potBusy, setPotBusy] = useState(false);
  React.useEffect(() => { api.get("/settings/pot-commun").then((r) => setPotCommun(r.data.enabled)).catch(() => {}); }, []);
  const togglePotCommun = async () => {
    const next = !potCommun;
    if (next && !window.confirm("Activer le Pot commun ?\n\nÀ partir de maintenant, un lead sans aucune action pendant 48h déclenchera un rappel au vendeur, et sans action à 72h il repartira automatiquement dans le pot commun (désassigné). À activer au moment du lancement.")) return;
    setPotBusy(true);
    try {
      const { data } = await api.put("/settings/pot-commun", { enabled: next });
      setPotCommun(data.enabled);
      toast.success(data.enabled ? "Pot commun activé 🚀 Le compteur d'inactivité démarre maintenant." : "Pot commun désactivé.");
    } catch (e) { toast.error("Modification impossible."); }
    finally { setPotBusy(false); }
  };
  const importKey = "kiosque-import-key-CHANGEME-2b2aa541438e5d4d";
  const importUrl = `${process.env.REACT_APP_BACKEND_URL}/api/prospects/import`;

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const resp = await api.get("/reports/weekly", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport-hebdo-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapport téléchargé");
    } catch (e) { toast.error("Génération impossible"); }
    finally { setBusy(false); }
  };

  const sendReportNow = async () => {
    if (!window.confirm("Envoyer le rapport hebdomadaire par email maintenant à la direction (admin, dirigeant, managers) ?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reports/weekly/send");
      if (data.skipped === "no_api_key") {
        toast.error("Envoi impossible : clé Resend manquante");
      } else if (data.sent > 0) {
        toast.success(`Rapport envoyé à ${data.sent} destinataire(s)`, { duration: 6000 });
      } else {
        const err = (data.errors && data.errors[0]) || "aucun destinataire";
        toast.warning(`Aucun email envoyé. ${err.includes("testing emails") ? "Domaine Resend non vérifié — envoi limité à l'adresse du compte." : err}`, { duration: 9000 });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const loadStats = async () => {
    const c = await api.get("/dashboard/commerce").catch(() => ({ data: null }));
    const a = await api.get("/dashboard/admin").catch(() => ({ data: null }));
    setStats({ commerce: c.data, admin: a.data });
  };

  React.useEffect(() => { loadStats(); }, []);

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copié"); };

  // ---- Demo mode ----
  const [demoStatus, setDemoStatus] = useState(null);
  const loadDemoStatus = () => api.get("/admin/demo-status").then(r => setDemoStatus(r.data)).catch(() => {});
  React.useEffect(() => { if (["admin","dirigeant"].includes(user?.role)) loadDemoStatus(); }, [user?.role]);

  const seedDemo = async () => {
    if (!window.confirm("Peupler l'application avec des données de démonstration ? (10 prospects + 3 dossiers signés · flag is_demo=true, effaçables ensuite)")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/seed-demo");
      toast.success(data.already_seeded ? data.message : `${data.prospects} prospects + ${data.dossiers} dossiers créés`);
      loadDemoStatus();
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const resetDemo = async () => {
    if (!window.confirm("⚠️ Effacer TOUTES les données de démonstration ?\n\nSeules les données marquées is_demo=true seront supprimées. Vos données réelles sont préservées.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo");
      const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`${total} enregistrements de démo supprimés`);
      loadDemoStatus();
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  // ---- Suck My Lead sync ----
  const [smlPreview, setSmlPreview] = useState(null);
  const [smlLoading, setSmlLoading] = useState(false);
  const [importProspects, setImportProspects] = useState(false);

  const previewSml = async () => {
    setSmlLoading(true);
    try {
      const { data } = await api.get("/admin/sync-preview");
      setSmlPreview(data);
      toast.success(`${data.conseillers.length} conseillers détectés`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSmlLoading(false); }
  };

  const runSml = async () => {
    const msg = importProspects
      ? `Importer les conseillers ET les prospects depuis Suck My Lead ?\n\nCela va créer/mettre à jour les utilisateurs et importer jusqu'à ~2600 prospects. Opération idempotente (pas de doublons).`
      : `Importer uniquement les conseillers depuis Suck My Lead ?`;
    if (!window.confirm(msg)) return;
    setSmlLoading(true);
    try {
      const { data } = await api.post("/admin/sync-suck-my-lead", null, { params: { import_prospects: importProspects } });
      const c = data.conseillers;
      let m = `✓ ${c.created} conseiller(s) créé(s), ${c.updated} mis à jour`;
      if (data.prospects && !data.prospects.skipped) {
        m += ` · ${data.prospects.created} prospects importés`;
      }
      toast.success(m, { duration: 8000 });
      if (data.default_password_for_new_users) {
        alert(`Mot de passe par défaut pour les nouveaux conseillers : ${data.default_password_for_new_users}\n\nCommuniquez-le à votre équipe. Chacun pourra le changer via un admin plus tard.`);
      }
      previewSml();
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSmlLoading(false); }
  };

  const curlExample = `curl -X POST ${importUrl} \\
  -H "Content-Type: application/json" \\
  -H "X-Import-Key: ${importKey}" \\
  -d '{
    "prenom": "Jean",
    "nom": "Dupont",
    "email": "jean.dupont@email.fr",
    "telephone": "0612345678",
    "programme": "Résidence des Ormes",
    "source": "gmail",
    "message": "Bonjour, je suis intéressé par...",
    "vendeur_email": "vendeur@agence.fr",
    "external_id": "gmail-msg-42"
  }'`;

  return (
    <div className="p-8" data-testid="reports-page">
      <Toaster position="top-right" richColors />
      <PageHeader
        title="Rapports & Intégrations"
        subtitle="Direction · Vue synthétique"
        actions={
          <>
            <a
              href="/Le_Kiosque_Immobilier_Presentation.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-presentation-pdf"
              className="bg-white border border-[#B8873A] text-[#111111] px-4 py-2 rounded-sm text-sm font-semibold hover:bg-[#B8873A]/10 transition-colors flex items-center gap-2"
            >
              <FileDown className="w-4 h-4" /> Présentation associés (PDF)
            </a>
            <PrimaryButton onClick={downloadPdf} disabled={busy} data-testid="download-weekly-pdf">
              <span className="flex items-center gap-2"><FileDown className="w-4 h-4" /> {busy ? "Génération…" : "Rapport hebdo PDF"}</span>
            </PrimaryButton>
            <SecondaryButton onClick={sendReportNow} disabled={busy} data-testid="send-weekly-report-now">
              <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Envoyer le rapport maintenant</span>
            </SecondaryButton>
          </>
        }
      />

      {stats && (
        <>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Vue Commerce</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <MetricCard label="Total prospects" value={stats.commerce?.total_prospects ?? 0} icon={Users} tone="primary" />
            <MetricCard label="Ventes signées" value={stats.commerce?.ventes_signees ?? 0} icon={Briefcase} tone="success" />
            <MetricCard label="À relancer" value={stats.commerce?.a_relancer ?? 0} tone="warning" />
            <MetricCard label="Prospects chauds" value={stats.commerce?.chauds ?? 0} tone="danger" />
          </div>

          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Vue BackOffice & Financier</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <MetricCard label="Commissions encaissées" value={formatEUR(stats.admin?.ca_encaisse ?? 0)} icon={PiggyBank} tone="success" />
            <MetricCard label="Commissions en attente" value={formatEUR(stats.admin?.ca_en_attente ?? 0)} icon={TrendingUp} tone="gold" />
            <MetricCard label="Financements en retard" value={stats.admin?.financement_en_retard ?? 0} tone="danger" />
            <MetricCard label="Recouvrement" value={stats.admin?.recouvrement ?? 0} tone="warning" />
          </div>
        </>
      )}

      {["admin","dirigeant"].includes(user?.role) && (
        <Section title="Pot commun — Leads non traités" actions={<Recycle className="w-4 h-4 text-[#B8873A]" />}>
          <div className="p-5" data-testid="pot-commun-setting">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-slate-600">
                  Quand c'est activé : un lead sans aucune action pendant <b>48h</b> envoie un rappel au vendeur ; sans action à <b>72h</b>, il repart automatiquement dans le <b>pot commun</b> (désassigné), où tous les vendeurs peuvent le récupérer.
                </p>
                <p className="text-xs text-slate-400 mt-1">À activer au moment du grand lancement. Désactivé, aucun lead n'est déplacé.</p>
              </div>
              <button
                onClick={togglePotCommun}
                disabled={potBusy}
                data-testid="pot-commun-toggle"
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${potCommun ? "bg-[#B8873A]" : "bg-slate-300"} disabled:opacity-50`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${potCommun ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            <div className="mt-3 text-xs font-semibold" data-testid="pot-commun-status">
              {potCommun ? <span className="text-emerald-700">● Activé</span> : <span className="text-slate-500">● Désactivé</span>}
            </div>
          </div>
        </Section>
      )}

      {["admin"].includes(user?.role) && (
        <Section title="Passage en beta — Nettoyage des comptes de test" actions={<RotateCcw className="w-4 h-4 text-red-600" />}>
          <div className="p-5 space-y-3">
            <div className="bg-red-50 border border-red-200 px-4 py-3 rounded-sm text-sm text-red-900">
              <strong>⚠️ Action définitive :</strong> supprime les 5 comptes de démonstration (Julie Vendeuse, Marc Manager, Sophie Dirigeante, Camille Secrétaire, Léo Facturation) <strong>+ toutes les données flag&eacute;es is_demo</strong>. Votre compte administrateur et les conseillers importés depuis Suck My Lead sont préservés. Idéal avant d&apos;utiliser l&apos;application en beta.
            </div>
            <SecondaryButton
              onClick={async () => {
                if (!window.confirm("Supprimer définitivement les 5 comptes de démo (Julie, Marc, Sophie, Camille, Léo) et toutes les données is_demo ?\n\nVotre compte admin et les conseillers Suck My Lead ne sont pas touchés.")) return;
                setBusy(true);
                try {
                  const { data } = await api.post("/admin/cleanup-fake-users");
                  toast.success(`${data.users_deleted} comptes + données de démo supprimés`, { duration: 6000 });
                  loadDemoStatus();
                  loadStats();
                } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                finally { setBusy(false); }
              }}
              disabled={busy}
              data-testid="cleanup-fake-users-btn"
              className="!text-red-700 !border-red-300 hover:!bg-red-50"
            >
              <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Nettoyer les comptes de démo</span>
            </SecondaryButton>
          </div>
        </Section>
      )}

      {["admin", "dirigeant"].includes(user?.role) && (
        <Section title="Synchronisation Suck My Lead — Équipe & Prospects" actions={
          <Link2 className="w-4 h-4 text-[#B8873A]" />
        }>
          <div className="p-5 space-y-4">
            <div className="bg-blue-50 border border-blue-200 px-4 py-3 rounded-sm text-sm text-blue-900 flex gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                Récupère automatiquement <strong>vos conseillers</strong> (et optionnellement vos <strong>prospects existants</strong>) depuis Suck My Lead. Opération 100 % <strong>lecture seule</strong> côté Suck My Lead — l&apos;app source n&apos;est jamais modifiée. Idempotent : aucun doublon.
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
              <SecondaryButton onClick={previewSml} disabled={smlLoading} data-testid="sml-preview-btn">
                <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4" /> {smlLoading ? "Chargement…" : "Prévisualiser"}</span>
              </SecondaryButton>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={importProspects} onChange={(e) => setImportProspects(e.target.checked)} data-testid="sml-import-prospects-toggle" />
                <span>Importer aussi les prospects (~2 600)</span>
              </label>
              <PrimaryButton onClick={runSml} disabled={smlLoading} data-testid="sml-sync-btn">
                <span className="flex items-center gap-2"><Link2 className="w-4 h-4" /> {smlLoading ? "Synchronisation…" : "Synchroniser"}</span>
              </PrimaryButton>
            </div>

            {smlPreview && (
              <div className="border border-slate-200 rounded-sm bg-white overflow-hidden">
                <div className="flex justify-between items-center px-4 py-2 bg-slate-50 border-b border-slate-200">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                    {smlPreview.conseillers.length} conseiller(s) détecté(s)
                  </div>
                  {smlPreview.prospects && !smlPreview.prospects.error && (
                    <div className="text-xs text-slate-600">
                      Prospects Suck My Lead : <strong>{smlPreview.prospects.total_in_sml}</strong> · déjà importés <strong>{smlPreview.prospects.already_imported}</strong> · à importer <strong className="text-[#B8873A]">{smlPreview.prospects.would_import}</strong>
                    </div>
                  )}
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-white text-slate-700 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold uppercase text-[10px] tracking-wider">Conseiller</th>
                      <th className="text-left px-4 py-2 font-semibold uppercase text-[10px] tracking-wider">Email</th>
                      <th className="text-left px-4 py-2 font-semibold uppercase text-[10px] tracking-wider">Statut</th>
                      <th className="text-right px-4 py-2 font-semibold uppercase text-[10px] tracking-wider">Ventes / mois</th>
                      <th className="text-right px-4 py-2 font-semibold uppercase text-[10px] tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {smlPreview.conseillers.map((c) => (
                      <tr key={c.sml_id} className="border-b border-slate-100 last:border-b-0" data-testid={`sml-preview-${c.sml_id}`}>
                        <td className="px-4 py-2 font-semibold text-[#111111]">{c.name}</td>
                        <td className="px-4 py-2 text-slate-600 text-xs">{c.email}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${c.statut === "disponible" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{c.statut || "—"}</span>
                        </td>
                        <td className="px-4 py-2 text-right font-semibold">{c.ventes_mois ?? 0}</td>
                        <td className="px-4 py-2 text-right">
                          <span className={`text-[10px] font-semibold uppercase tracking-wider ${c.action === "create" ? "text-blue-700" : "text-slate-500"}`}>
                            {c.action === "create" ? "+ Créer" : "↻ Mettre à jour"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Section>
      )}

      {["admin", "dirigeant"].includes(user?.role) && (
        <Section title="Mode démonstration — Réunion partenaires" actions={
          demoStatus?.is_seeded ? (
            <span className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">● Données de démo actives</span>
          ) : (
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">○ Aucune donnée de démo</span>
          )
        }>
          <div className="p-5 space-y-4">
            <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-sm text-sm text-amber-900 flex gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <strong>Pour votre réunion :</strong> peuplez l&apos;application avec 10 prospects français réalistes (à divers stades du pipeline) et 3 dossiers signés (financement, notaire, factures). <strong>Effaçables en 1 clic</strong> sans toucher aux données réelles (flag <code className="bg-amber-100 px-1">is_demo=true</code>).
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <PrimaryButton onClick={seedDemo} disabled={busy || demoStatus?.is_seeded} data-testid="seed-demo-button">
                <span className="flex items-center gap-2"><PlayCircle className="w-4 h-4" /> Activer les données de démo</span>
              </PrimaryButton>
              <SecondaryButton onClick={resetDemo} disabled={busy || !demoStatus?.is_seeded} data-testid="reset-demo-button" className="!text-red-700 !border-red-300 hover:!bg-red-50">
                <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Réinitialiser (effacer démo)</span>
              </SecondaryButton>
            </div>
            {demoStatus?.is_seeded && (
              <div className="text-xs text-slate-600">
                <strong>{demoStatus.demo_prospects}</strong> prospects et <strong>{demoStatus.demo_dossiers}</strong> dossiers de démo actuellement en base.
              </div>
            )}
          </div>
        </Section>
      )}

      {["admin", "dirigeant"].includes(user?.role) && (
        <Section title="Intégration Suck My Lead — Webhook d'import">
          <div className="p-5 space-y-4">
            <div className="bg-blue-50 border border-blue-200 px-4 py-3 rounded-sm text-sm text-blue-900 flex gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                Configurez votre app Suck My Lead pour envoyer les prospects extraits de Gmail vers cet endpoint.
                Les prospects assignés (via <code className="bg-blue-100 px-1">vendeur_email</code>) apparaîtront directement dans l&apos;espace Commerce.
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">URL du webhook</div>
              <div className="flex gap-2">
                <input readOnly value={importUrl} className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono bg-slate-50" data-testid="webhook-url" />
                <SecondaryButton onClick={() => copy(importUrl)}><Copy className="w-4 h-4" /></SecondaryButton>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Clé d&apos;authentification (Header <code>X-Import-Key</code>)</div>
              <div className="flex gap-2">
                <input readOnly value={importKey} className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono bg-slate-50" data-testid="webhook-key" />
                <SecondaryButton onClick={() => copy(importKey)}><Copy className="w-4 h-4" /></SecondaryButton>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">⚠️ Modifiable dans <code>/app/backend/.env</code> (variable <code>IMPORT_API_KEY</code>) — pensez à la changer avant la mise en production.</div>
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Exemple d&apos;appel (cURL)</div>
              <pre className="bg-slate-900 text-slate-100 text-xs p-4 rounded-sm overflow-x-auto whitespace-pre">{curlExample}</pre>
              <SecondaryButton onClick={() => copy(curlExample)} className="mt-2"><span className="flex items-center gap-1"><Copy className="w-3 h-3" /> Copier l&apos;exemple</span></SecondaryButton>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Champs acceptés (flexibles)</div>
              <div className="text-sm text-slate-700 grid grid-cols-1 md:grid-cols-2 gap-y-1">
                <div>• <code className="bg-slate-100 px-1">prenom</code> / <code className="bg-slate-100 px-1">first_name</code></div>
                <div>• <code className="bg-slate-100 px-1">nom</code> / <code className="bg-slate-100 px-1">last_name</code></div>
                <div>• <code className="bg-slate-100 px-1">name</code> (nom complet, split auto)</div>
                <div>• <code className="bg-slate-100 px-1">email</code></div>
                <div>• <code className="bg-slate-100 px-1">telephone</code> / <code className="bg-slate-100 px-1">phone</code></div>
                <div>• <code className="bg-slate-100 px-1">programme</code> / <code className="bg-slate-100 px-1">project</code></div>
                <div>• <code className="bg-slate-100 px-1">source</code>, <code className="bg-slate-100 px-1">message</code></div>
                <div>• <code className="bg-slate-100 px-1">vendeur_email</code> ou <code className="bg-slate-100 px-1">vendeur_id</code></div>
                <div>• <code className="bg-slate-100 px-1">external_id</code> (idempotence)</div>
                <div>• <code className="bg-slate-100 px-1">budget</code>, <code className="bg-slate-100 px-1">budget_min</code>, <code className="bg-slate-100 px-1">budget_max</code></div>
              </div>
              <div className="text-[11px] text-slate-500 mt-3">
                Les prospects importés arrivent avec statut <strong>&quot;nouveau&quot;</strong>. Si <code className="bg-slate-100 px-1">vendeur_email</code> correspond à un utilisateur du Kiosque, le prospect est automatiquement assigné à ce vendeur.
                Un appel avec le même <code className="bg-slate-100 px-1">external_id</code> qu&apos;un prospect existant renvoie <code className="bg-slate-100 px-1">duplicate_skipped</code>.
              </div>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
